import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { MpReport } from '@accounting/shared';
import { ManualSessionForm } from '../../components/participation/ManualSessionForm';
import { SessionEditor, exceptionText, formatInstant } from '../../components/participation/SessionEditor';
import {
  useLockParticipationYear,
  useParticipationExceptions,
  useParticipationReport,
  useParticipationSession,
  useUnlockParticipationYear,
} from '../../hooks/useParticipation';
import { ApiError, downloadFile } from '../../lib/api';
import { formatAccountingDate } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/participation/')({
  component: ParticipationPage,
});

const THIS_YEAR = new Date().getFullYear();
const YEARS = [0, 1, 2, 3, 4].map((n) => THIS_YEAR - n);

function ParticipationPage() {
  const [year, setYear] = useState(THIS_YEAR);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const report = useParticipationReport(year);
  const locked = report.data ? isLocked(report.data) : false;

  return (
    <div className="mp-page">
      <div className="mp-page__head">
        <div>
          <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>Participation hours</h1>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Hours at each business, from your phone&rsquo;s arrive and leave events. Clean visits count
            on their own; only the ones below the heading &ldquo;Needs you&rdquo; wait for anything.
          </p>
        </div>
        <div className="mp-page__controls">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Tax year">
            {YEARS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <Link to="/settings/participation" className="btn-secondary">
            Set up
          </Link>
        </div>
      </div>

      <ExceptionsCard lockedThroughYear={report.data?.lockedThroughYear ?? null} />

      {report.isLoading && <p className="muted">Loading…</p>}
      {report.error && <p className="field-error">Failed to load the hours report.</p>}

      {report.data && (
        <>
          <SummaryCard report={report.data} />

          <div className="card mp-card">
            <div className="mp-card__head">
              <h2>Log</h2>
              {!locked && !adding && (
                <button className="btn-secondary" onClick={() => setAdding(true)}>
                  + Add time by hand
                </button>
              )}
            </div>
            {adding && <ManualSessionForm onDone={() => setAdding(false)} />}
            {editingId && (
              <EditorById sessionId={editingId} locked={locked} onDone={() => setEditingId(null)} />
            )}
            <LogTable report={report.data} onEdit={setEditingId} />
          </div>

          <NotCountedCard report={report.data} onEdit={setEditingId} />
          <RecordCard report={report.data} />
        </>
      )}

      <p className="muted" style={{ fontSize: 12 }}>Record-keeping tool only. Not tax advice.</p>
    </div>
  );
}

function isLocked(report: MpReport): boolean {
  return report.lockedThroughYear !== null && report.year <= report.lockedThroughYear;
}

function EditorById({ sessionId, locked, onDone }: { sessionId: string; locked: boolean; onDone: () => void }) {
  const { data, isLoading, error } = useParticipationSession(sessionId);
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !data) return <p className="field-error">Could not load that session.</p>;
  return <SessionEditor key={data.session.id} session={data.session} locked={locked} onDone={onDone} />;
}

// ── Needs you ──────────────────────────────────────────────────────────────

function ExceptionsCard({ lockedThroughYear }: { lockedThroughYear: number | null }) {
  const { data = [], error } = useParticipationExceptions();
  const [openId, setOpenId] = useState<string | null>(null);

  if (error) return <p className="field-error">Failed to load the sessions that need you.</p>;
  if (data.length === 0) return null;

  return (
    <div className="card mp-card mp-card--attention">
      <div className="mp-card__head">
        <h2>Needs you</h2>
        <span className="tag warn">{data.length}</span>
      </div>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        These could not be paired cleanly, so they are not in any total yet.
      </p>
      {data.map((s) => {
        const when = s.startAt ?? s.endAt;
        const yearOf = when ? new Date(when).getFullYear() : null;
        const locked = lockedThroughYear !== null && yearOf !== null && yearOf <= lockedThroughYear;
        return (
          <div key={s.id} className="mp-exception">
            {openId === s.id ? (
              <SessionEditor session={s} locked={locked} onDone={() => setOpenId(null)} />
            ) : (
              <button className="mp-exception__row" onClick={() => setOpenId(s.id)}>
                <span>
                  <strong>{s.companyName}</strong>
                  <span className="muted"> {s.placeName ?? ''}</span>
                  <br />
                  <span style={{ fontSize: 13 }}>
                    {formatInstant(s.startAt)} to {formatInstant(s.endAt)}
                  </span>
                  <br />
                  <span className="muted" style={{ fontSize: 12 }}>{exceptionText(s)}</span>
                </span>
                <span className="btn-secondary">Fix</span>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Summary ────────────────────────────────────────────────────────────────

const hoursOf = (minutes: number) => (minutes / 60).toFixed(2); // display only

function SummaryCard({ report }: { report: MpReport }) {
  return (
    <div className="card mp-card">
      <div className="mp-card__head">
        <h2>{report.year} by business</h2>
        <ExportButtons year={report.year} />
      </div>
      {report.rows.length === 0 ? (
        <p className="muted">No confirmed hours in {report.year} yet.</p>
      ) : (
        <div className="mp-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Business</th>
                <th className="right">On site</th>
                <th className="right">Travel</th>
                <th className="right">With travel</th>
                <th className="right mp-wide">Self</th>
                <th className="right mp-wide">Spouse</th>
                <th className="right mp-wide">From phone</th>
                <th className="right mp-wide">By hand</th>
                {report.isCurrentYear && <th className="right">Pace to Dec 31</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.companyId}>
                  <td>{r.companyName}</td>
                  <td className="right"><strong>{r.totalHours}</strong></td>
                  <td className="right muted">{r.travelHours}</td>
                  <td className="right">{r.withTravelHours}</td>
                  <td className="right mp-wide">{hoursOf(r.selfMinutes)}</td>
                  <td className="right mp-wide">{hoursOf(r.spouseMinutes)}</td>
                  <td className="right mp-wide">{hoursOf(r.autoMinutes)}</td>
                  <td className="right mp-wide">{hoursOf(r.manualMinutes)}</td>
                  {report.isCurrentYear && <td className="right">{r.projectedHours}</td>}
                  <td>
                    {r.over500 ? (
                      <span className="tag ok">over 500</span>
                    ) : r.over500WithTravel ? (
                      <span className="tag info">over 500 with travel</span>
                    ) : r.over100 ? (
                      <span className="tag info">over 100</span>
                    ) : r.over100WithTravel ? (
                      <span className="tag">over 100 with travel</span>
                    ) : (
                      <span className="tag">under 100</span>
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td><strong>Total</strong></td>
                <td className="right"><strong>{report.totalHours}</strong></td>
                <td className="right muted">{report.travelHours}</td>
                <td className="right"><strong>{report.withTravelHours}</strong></td>
                <td colSpan={report.isCurrentYear ? 6 : 5}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
        On site comes from the phone. Travel is your estimate per location, added once per location
        per day, and is never mixed into the on-site figure. Pace and the 100 and 500 marks use
        on-site hours.
      </p>
      {report.isCurrentYear && (
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
          Pace is a straight line: hours so far over {report.daysElapsed} days, carried to{' '}
          {report.daysInYear}. Times use {report.timeZone}.
        </p>
      )}
    </div>
  );
}

function ExportButtons({ year }: { year: number }) {
  const [busy, setBusy] = useState<'xlsx' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (format: 'xlsx' | 'pdf') => {
    setBusy(format);
    setError(null);
    downloadFile('/participation/report/export', { year: String(year), format }, `participation-log-${year}.${format}`)
      .catch(() => setError('Export failed'))
      .finally(() => setBusy(null));
  };

  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      {error && <span className="field-error" style={{ margin: 0 }}>{error}</span>}
      <button className="btn-secondary" onClick={() => run('xlsx')} disabled={busy !== null}>
        {busy === 'xlsx' ? 'Building…' : 'Excel'}
      </button>
      <button className="btn-secondary" onClick={() => run('pdf')} disabled={busy !== null}>
        {busy === 'pdf' ? 'Building…' : 'PDF'}
      </button>
    </span>
  );
}

// ── Log ────────────────────────────────────────────────────────────────────

function LogTable({ report, onEdit }: { report: MpReport; onEdit: (id: string) => void }) {
  if (report.log.length === 0) return <p className="muted">Nothing logged in {report.year} yet.</p>;
  // Newest first on screen; the exports run oldest first, the way a log is read.
  const rows = [...report.log].reverse();
  return (
    <div className="mp-scroll">
      <table className="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th className="right">Hours</th>
            <th>Business</th>
            <th className="mp-wide">Place</th>
            <th className="mp-wide">Note</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.sessionId}-${r.date}-${i}`} onClick={() => onEdit(r.sessionId)} style={{ cursor: 'pointer' }}>
              <td style={{ whiteSpace: 'nowrap' }}>{formatAccountingDate(r.date)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {r.start} to {r.end}
              </td>
              <td className="right">
                {r.hours}
                {r.travelMinutes > 0 && <span className="muted"> +{r.travelMinutes}m travel</span>}
              </td>
              <td>
                {r.companyName}
                {r.participant === 'spouse' && <span className="muted"> (spouse)</span>}
              </td>
              <td className="mp-wide">{r.placeName ?? ''}</td>
              <td className="mp-wide">{r.note ?? ''}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {r.source === 'manual' && <span className="tag warn">manual</span>}{' '}
                {r.isEdited && <span className="tag">edited</span>}{' '}
                {r.attachmentCount > 0 && <span className="tag info">{r.attachmentCount} file(s)</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotCountedCard({ report, onEdit }: { report: MpReport; onEdit: (id: string) => void }) {
  if (report.notCounted.length === 0) return null;
  return (
    <div className="card mp-card">
      <div className="mp-card__head">
        <h2>Not counted</h2>
        <span className="muted" style={{ fontSize: 13 }}>{report.notCounted.length}</span>
      </div>
      <div className="mp-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Business</th>
              <th className="right">Minutes</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {report.notCounted.map((r) => (
              <tr key={r.sessionId} onClick={() => onEdit(r.sessionId)} style={{ cursor: 'pointer' }}>
                <td style={{ whiteSpace: 'nowrap' }}>{r.date ? formatAccountingDate(r.date) : ''}</td>
                <td>{r.companyName}</td>
                <td className="right">{r.minutes ?? ''}</td>
                <td>{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Record integrity and closing the year ──────────────────────────────────

function RecordCard({ report }: { report: MpReport }) {
  const lock = useLockParticipationYear();
  const unlock = useUnlockParticipationYear();
  const [reason, setReason] = useState('');
  const [reopening, setReopening] = useState(false);
  const locked = isLocked(report);
  const error = lock.error ?? unlock.error;
  const errorMessage =
    error instanceof ApiError ? ((error.body as { error?: string } | null)?.error ?? 'Request failed') : error?.message;

  return (
    <div className="card mp-card">
      <div className="mp-card__head">
        <h2>Record</h2>
        {report.chain.valid ? (
          <span className="tag ok">event log verified ({report.chain.count} events)</span>
        ) : (
          <span className="tag bad">event log TAMPERED at #{report.chain.problems[0]?.sequence}</span>
        )}
      </div>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        {report.chain.eventsInYear} raw events received in {report.year}. {report.edits.length} field(s)
        edited by hand; every one is listed in the export with its reason.
      </p>

      {locked ? (
        <>
          <p style={{ fontSize: 14 }}>
            <span className="tag warn">closed</span> {report.year} is closed. Its sessions are read only.
          </p>
          {!reopening ? (
            <button className="btn-secondary" onClick={() => setReopening(true)}>
              Reopen {report.year}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={300}
                placeholder="Why it is being reopened (kept in the audit log)"
                style={{ flex: '1 1 260px' }}
              />
              <button
                className="btn-danger"
                disabled={reason.trim().length < 3 || unlock.isPending}
                onClick={() =>
                  unlock.mutate(
                    { year: report.year, reason: reason.trim() },
                    { onSuccess: () => { setReopening(false); setReason(''); } },
                  )
                }
              >
                Reopen
              </button>
              <button className="btn-secondary" onClick={() => setReopening(false)}>
                Cancel
              </button>
            </div>
          )}
        </>
      ) : (
        !report.isCurrentYear && (
          <button
            className="btn-secondary"
            disabled={lock.isPending}
            onClick={() => {
              if (confirm(`Close ${report.year}? Its sessions become read only until the year is reopened with a reason.`)) {
                lock.mutate(report.year);
              }
            }}
          >
            Close {report.year}
          </button>
        )
      )}
      {errorMessage && <div className="field-error">{errorMessage}</div>}
    </div>
  );
}
